import { Warning } from '@tempo-io/sui-icons';
import { Tag } from '@tempo-io/sui-tag';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getCustomFieldDateDisplay, removeSystemFieldNamePrefix } from '../../utils/Utilities';
import { CustomFieldRating } from './CustomFieldRating';
import { getCurrencySymbol } from './NumberFieldInput';
import * as Styled from './Styled';

export type TranslationFunction = TFunction;

type CustomFieldValueProps = {
  fieldType?: string;
  values?: string[];
  textOnly?: string;
  minimize?: boolean;
};

export const getFormulaDataString = (t: TFunction, values: string[]) => {
  values = values.map((value) => removeSystemFieldNamePrefix(value));
  let value = values[0];
  let hasError = false;
  if (values && values.length > 2) {
    hasError = true;
    const moreCount = values.length - 3;
    let errorText: string = t(($) => $.intake.general.missingField, {
      fieldName: values[2],
    });
    if (moreCount >= 2) {
      errorText = t(($) => $.intake.general.missingFieldAndNMore, {
        fieldName: values[2],
        count: moreCount,
      });
    } else if (moreCount === 1) {
      errorText = t(($) => $.intake.general.missingFieldAnd, {
        fieldName: values[2],
        fieldName2: values[3],
      });
    }

    value = t(($) => $.intake.general.formulaValueCannotBeCalculated, {
      error: errorText,
    });
  } else if (values && values.length > 1) {
    hasError = true;
    value = t(($) => $.intake.general.formulaValueCannotBeCalculated, {
      error: `Error: ${values[1]}`,
    });
  }
  return { value, hasError };
};

const getFormulaDataStringValueOnly = (t: TFunction, values: string[]) => {
  const { value } = getFormulaDataString(t, values ?? []);
  return value;
};

export const getCustomFieldValueAsString = (
  t: TFunction,
  fieldType: string,
  values?: string[],
  currency?: string | null | undefined,
) => {
  switch (fieldType) {
    case 'RATING':
      return (
        <CustomFieldRating
          data-testid="fim3-custom-field-input-rating-read-only"
          value={values ? parseInt(values[0] ?? '0', 10) : 0}
          disabled
        />
      );
    case 'FORMULA':
      return getFormulaDataStringValueOnly(t, values ?? []);
    case 'CURRENCY':
      return values?.[0] ? getCurrencySymbol(currency) + values[0] : '';
    case 'DATE':
    case 'DATE_RANGE':
      return values ? values.map((el) => getCustomFieldDateDisplay(el)).join(' - ') : '';
    case 'FIBONACCI':
      return values?.[0] ?? '';
    default:
      return values?.filter((el) => !!el).join(', ');
  }
};

export const CustomFieldValue = ({
  fieldType,
  textOnly,
  values,
  minimize,
}: CustomFieldValueProps) => {
  const { t } = useTranslation();

  if (fieldType === undefined) return;

  const value = values ? (values[0] ?? '') : '';

  if (value === '' && fieldType !== 'FORMULA') return;

  const { value: formulaText, hasError } = getFormulaDataString(t, values ?? []);

  if (textOnly && fieldType !== 'FORMULA') {
    return <p className="text-sm leading-normal">{values?.toString()}</p>;
  }

  switch (fieldType) {
    case 'LIST':
      return (
        <Styled.FlexDiv>
          {values?.map((el) => (
            <Tag
              className="fs-exclude"
              data-dd-action-name="FIM 3 Custom Field - value redracted"
              key={el}
              size="sm"
            >
              {el}
            </Tag>
          ))}
        </Styled.FlexDiv>
      );
    case 'PERCENT':
      return (
        <Styled.PercentContainer>
          <Styled.PercentBar
            className="fs-exclude"
            data-dd-action-name="FIM 3 Custom Field - value redacted"
            percent={value ? parseInt(value, 10) : 0}
          >
            {value ? `${value}%` : null}
          </Styled.PercentBar>
        </Styled.PercentContainer>
      );
    case 'RATING':
      return (
        <Styled.FieldRating
          data-dd-action-name="FIM 3 Custom Field - value redacted"
          className="fs-exclude"
          value={parseInt(value, 10)}
          readOnly
        />
      );
    case 'DATE':
    case 'DATE_RANGE':
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - value redacted"
        >
          {values ? values.map((el) => getCustomFieldDateDisplay(el)).join(' - ') : ''}
        </p>
      );
    case 'FIBONACCI':
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - fibonacci value redacted"
        >
          {value}
        </p>
      );
    case 'FORMULA':
      if (minimize && hasError) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="fs-exclude inline-flex"
                data-dd-action-name="FIM 3 Custom Field - formula redacted"
              >
                <Warning className="text-icon-warning" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{formulaText}</TooltipContent>
          </Tooltip>
        );
      }

      return hasError ? (
        <Styled.FlexDiv>
          <Warning className="text-icon-warning" />
          <p
            className="fs-exclude text-sm leading-normal"
            data-dd-action-name="FIM 3 Custom Field - formula redacted"
          >
            {formulaText}
          </p>
        </Styled.FlexDiv>
      ) : (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - formula redacted"
        >
          {formulaText}
        </p>
      );
    default:
      return (
        <p
          className="fs-exclude text-sm leading-normal"
          data-dd-action-name="FIM 3 Custom Field - formula redacted"
        >
          {value}
        </p>
      );
  }
};
